// API client para apps.sales LeadImport en mcp_django.
// Backend: /api/v1/admin/leads/import/* (auth Bearer JWT, scope leads:import:*)

const DJANGO_PROXY = '/django-api';

import { ensureFreshToken } from '@/lib/tokenRefresh';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const jwt = await getJwt();
  const headers: Record<string, string> = { Authorization: `Bearer ${jwt}` };
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function djangoUpload<T = any>(path: string, formData: FormData): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(err.detail || `Upload failed: ${res.status}`, res.status);
  }
  return res.json();
}

const BASE = '/api/v1/admin/leads/import';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface PreviewResult {
  import_id: string;
  total_rows: number;
  inferred_columns: string[];
  sample_rows: Record<string, string>[];
  suggested_mapping: Record<string, string>;
  llm_used: boolean;
  llm_error: string;
}

export interface CommitResult {
  import_id: string;
  queued: boolean;
  detail: string;
}

export interface ImportSummary {
  id: string;
  file_name: string;
  total_rows: number;
  leads_created: number;
  leads_skipped: number;
  presentations_generated: number;
  landings_generated: number;
  status: string;
  source_label: string;
  created_at: string;
}

export interface ImportDetail extends ImportSummary {
  inferred_columns: string[];
  sample_rows: Record<string, string>[];
  suggested_mapping: Record<string, string>;
  confirmed_mapping: Record<string, string>;
  errors: { row_index: number; error_message: string }[];
  created_by_email: string;
}

// ═══════════════════════════════════════════════════════════════
// Endpoints
// ═══════════════════════════════════════════════════════════════

export async function previewImport(file: File, sourceLabel = ''): Promise<PreviewResult> {
  const fd = new FormData();
  fd.append('file', file);
  if (sourceLabel) fd.append('source_label', sourceLabel);
  return djangoUpload<PreviewResult>(`${BASE}/preview`, fd);
}

export async function commitImport(
  importId: string,
  file: File,
  confirmedMapping: Record<string, string>,
): Promise<CommitResult> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('confirmed_mapping_json', JSON.stringify(confirmedMapping));
  return djangoUpload<CommitResult>(`${BASE}/${importId}/commit`, fd);
}

export async function listImports(limit = 50): Promise<ImportSummary[]> {
  return djangoFetch<ImportSummary[]>(`${BASE}/?limit=${limit}`);
}

export async function getImport(importId: string): Promise<ImportDetail> {
  return djangoFetch<ImportDetail>(`${BASE}/${importId}`);
}

// ═══════════════════════════════════════════════════════════════
// Presentations · LeadPresentation listing (kind='solution_landing'
// devuelve los landings públicos generados post-import).
// ═══════════════════════════════════════════════════════════════

export type PresentationDTO = {
  id: string;
  lead_id: string;
  status: string;
  html_url: string;
  focus: string;
  email_sent_at?: string | null;
  email_recipients?: string[];
  created_at: string;
  kind: 'presentation' | 'solution_landing';
  slug: string;
  landing_path: string;
  lead_name: string;
  lead_email: string;
  lead_phone: string;
  lead_business_context: Record<string, any>;
};

export async function listPresentations(params: {
  kind?: 'presentation' | 'solution_landing';
  lead_id?: string;
  limit?: number;
} = {}): Promise<PresentationDTO[]> {
  const q = new URLSearchParams();
  if (params.kind) q.set('kind', params.kind);
  if (params.lead_id) q.set('lead_id', params.lead_id);
  if (params.limit != null) q.set('limit', String(params.limit));
  const qs = q.toString();
  return djangoFetch<PresentationDTO[]>(`/api/v1/admin/presentations/${qs ? `?${qs}` : ''}`);
}

// ═══════════════════════════════════════════════════════════════
// Leads CRM · Phase 2 (assignment + reassign + inline edit + audit)
// ═══════════════════════════════════════════════════════════════

export type LeadAuditDTO = {
  id: string;
  action: string;
  actor_user_id: string;
  actor_email: string;
  actor_kind: string;
  diff: Record<string, { before: any; after: any }>;
  note: string;
  created_at: string;
};

export type LeadDTO = {
  id: string;
  org_id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  source: string;
  status: string;
  notes: string;
  business_context: Record<string, any>;
  extra: Record<string, any>;
  assigned_to_user_id: string;
  assigned_to_email: string;
  assigned_at: string | null;
  created_at: string;
  updated_at: string;
  landing_slug: string;
  landing_path: string;
  presentations_count: number;
  audit_events?: LeadAuditDTO[];
  /** Canal de origen (mcp_django apps.sales.attribution). Vacío = sin registrar. */
  canal?: string;
  /** Etiqueta en español que manda el backend (single-source; no se traduce aquí). */
  canal_etiqueta?: string;
  /** Primer toque de la visita: utm_*, referrer_domain, landing_path, ts, gclid/fbclid ("1"). */
  attribution?: Record<string, string>;
};

const LEADS_BASE = '/api/v1/admin/sales/leads';

export interface LeadListParams {
  mine?: boolean;
  assignee?: string;
  status?: string;
  search?: string;
  has_landing?: boolean;
  /** Canal de origen; `sin_canal` = leads sin canal registrado. */
  canal?: string;
  limit?: number;
  offset?: number;
}

function leadFilterQS(params: LeadListParams): URLSearchParams {
  const q = new URLSearchParams();
  if (params.mine) q.set('mine', 'true');
  if (params.assignee) q.set('assignee', params.assignee);
  if (params.status) q.set('status', params.status);
  if (params.search) q.set('search', params.search);
  if (params.has_landing != null) q.set('has_landing', String(params.has_landing));
  if (params.canal) q.set('canal', params.canal);
  return q;
}

export async function listLeads(params: LeadListParams = {}): Promise<LeadDTO[]> {
  const q = leadFilterQS(params);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return djangoFetch<LeadDTO[]>(`${LEADS_BASE}/${qs ? `?${qs}` : ''}`);
}

/** Total de leads que matchean los filtros (para paginación server-side).
 *  Defensivo: si el backend aún no tiene /count desplegado, devuelve null
 *  y la UI cae a paginación sin total. */
export async function countLeads(params: LeadListParams = {}): Promise<number | null> {
  try {
    const qs = leadFilterQS(params).toString();
    const r = await djangoFetch<{ total: number }>(`${LEADS_BASE}/count${qs ? `?${qs}` : ''}`);
    return r.total;
  } catch {
    return null;
  }
}

export type CanalConteoDTO = { canal: string; etiqueta: string; total: number };
export type DescargaConteoDTO = { slug: string; total: number; ligadas_a_lead: number; por_canal: Record<string, number> };
export type LeadsPorCanalDTO = {
  org_id: string;
  desde: string | null;
  hasta: string | null;
  total: number;
  por_canal: CanalConteoDTO[];
  descargas: DescargaConteoDTO[];
};

/** Conteo por canal + descargas por recurso (totales calculados en el backend).
 *  Defensivo: si el backend aún no tiene /canales desplegado, devuelve null. */
export async function countLeadsByChannel(params: { desde?: string; hasta?: string } = {}): Promise<LeadsPorCanalDTO | null> {
  try {
    const q = new URLSearchParams();
    if (params.desde) q.set('desde', params.desde);
    if (params.hasta) q.set('hasta', params.hasta);
    const qs = q.toString();
    return await djangoFetch<LeadsPorCanalDTO>(`${LEADS_BASE}/canales${qs ? `?${qs}` : ''}`);
  } catch {
    return null;
  }
}

export async function getLead(leadId: string): Promise<LeadDTO> {
  return djangoFetch<LeadDTO>(`${LEADS_BASE}/${leadId}`);
}

export async function patchLead(
  leadId: string,
  patch: Partial<Pick<LeadDTO, 'name' | 'email' | 'phone' | 'message' | 'status' | 'notes'>>,
): Promise<LeadDTO> {
  return djangoFetch<LeadDTO>(`${LEADS_BASE}/${leadId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function reassignLead(
  leadId: string,
  user_id: string,
  email = '',
): Promise<LeadDTO> {
  return djangoFetch<LeadDTO>(`${LEADS_BASE}/${leadId}/reassign`, {
    method: 'POST',
    body: JSON.stringify({ user_id, email }),
  });
}

export async function addLeadNote(leadId: string, note: string): Promise<LeadDTO> {
  return djangoFetch<LeadDTO>(`${LEADS_BASE}/${leadId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

/** Alta manual de un lead (POST /admin/sales/leads/, org del JWT). Si ya existía
 *  con el mismo teléfono NO se duplica: `created=false` y se completa el registro. */
export type CreateLeadResult = { id: string; created: boolean; status: string; reabierto?: boolean };

export async function createLead(payload: {
  name?: string; email?: string; phone?: string; message?: string; source?: string;
}): Promise<CreateLeadResult> {
  return djangoFetch<CreateLeadResult>(`${LEADS_BASE}/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export type RefireEnrichResult = {
  dispatched: number;
  skipped_already_have_landing: number;
  org_id: string;
};

export async function refireEnrichChain(params: {
  source_startswith?: string;
  status?: string;
  only_without_landing?: boolean;
  limit?: number;
} = {}): Promise<RefireEnrichResult> {
  return djangoFetch<RefireEnrichResult>(`${LEADS_BASE}/refire-enrich`, {
    method: 'POST',
    body: JSON.stringify({
      source_startswith: params.source_startswith || '',
      status: params.status || '',
      only_without_landing: params.only_without_landing !== false,
      limit: params.limit ?? 1000,
    }),
  });
}

// ═══════════════════════════════════════════════════════════════
// Mapping helpers — campos canónicos del backend
// ═══════════════════════════════════════════════════════════════

export const CANONICAL_FIELDS: { value: string; label: string; group: string }[] = [
  { value: '', label: '— Ignorar columna —', group: '' },
  { value: 'name', label: 'Nombre', group: 'Lead' },
  { value: 'email', label: 'Email', group: 'Lead' },
  { value: 'phone', label: 'Teléfono', group: 'Lead' },
  { value: 'message', label: 'Mensaje / nota', group: 'Lead' },
  { value: 'source', label: 'Origen específico', group: 'Lead' },
  { value: 'company_name', label: 'Empresa del lead', group: 'Contexto de negocio' },
  { value: 'industry', label: 'Industria', group: 'Contexto de negocio' },
  { value: 'role', label: 'Cargo / rol', group: 'Contexto de negocio' },
  { value: 'company_size', label: 'Tamaño empresa', group: 'Contexto de negocio' },
  { value: 'pain_hint', label: 'Pista de dolor de negocio', group: 'Contexto de negocio' },
];
